import "./SealRegistrationPage.scss";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { Paths } from "../../../paths";
import { AppSeo } from "../../../components/app-seo/AppSeo";
import { useSealRegistration, SealRegistrationFormData } from "./SealRegistrationContext";
import { sealRegistrationSchema } from "./validation/sealRegistrationValidation";
import { SealRegistrationStepIndicator } from "./SealRegistrationStepIndicator";
import { SealInfoBox } from "./components/SealInfoBox";
import { YourInformationSection } from "./sections/YourInformationSection";
import { RegistrationDetailsSection } from "./sections/RegistrationDetailsSection";
import { IntendedSealUseSection } from "./sections/IntendedSealUseSection";
import { RegisteredVineyardsSection } from "./sections/RegisteredVineyardsSection";
import { ProgramAttestationsSection } from "./sections/ProgramAttestationsSection";
import { CommunicationPreferencesSection } from "./sections/CommunicationPreferencesSection";
import { OptionalDonationSection } from "./sections/OptionalDonationSection";
import { ConferenceInterestSection } from "./sections/ConferenceInterestSection";
import { RegistrationTotalSection } from "./sections/RegistrationTotalSection";
import { Box } from "../../../components/styled-system/Box";

export const SealRegistrationPage = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useSealRegistration();

  const handleSubmit = (values: SealRegistrationFormData) => {
    dispatch({ type: "SET_FORM_DATA", payload: values });
    navigate(Paths.sealPayment);
  };

  return (
    <>
      <AppSeo
        title="OVR Seal Registration"
        description="Register your wine brand, vineyard, or business for the OVR Seal program to certify wines made from old-vine vineyards."
        keywords="OVR seal, old vine seal, wine registration, old vine registry, seal program registration, wine brand registration"
      />
      <div className="about-layout">
        <div className="seal-registration-content">
          <SealRegistrationStepIndicator currentStep={1} />

          <h1 className="title-large">Register to Use the OVR Seal</h1>
          <p className="description-text">
            Register one wine brand, vineyard business, importing business, or
            retail business for the OVR Seal program. Fields marked with an
            asterisk (*) are required.
          </p>

          <SealInfoBox>
            <p className="description-text">
            Please allow 1–3 weeks for registration and vineyard
            review/verification. Payment does not authorize immediate use of the
            seal. You may begin using the seal only after OVR confirms your
            registration and provides the official artwork.</p>
          </SealInfoBox>

          <Formik<SealRegistrationFormData>
            initialValues={state.formData}
            validationSchema={sealRegistrationSchema}
            validateOnChange={true}
            validateOnBlur={false}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form>
                <Box display="flex" flexDirection="column" maxWidth="600px">
                  <YourInformationSection />
                  <RegistrationDetailsSection />
                  <IntendedSealUseSection />
                  <RegisteredVineyardsSection />
                  <ProgramAttestationsSection />
                  <CommunicationPreferencesSection />
                  <OptionalDonationSection />
                  <ConferenceInterestSection />
                  <RegistrationTotalSection />

                  <p className="form-fields-sub-text" style={{ marginTop: "16px" }}>
                    After payment, your registration will be submitted for
                    review. Please allow 1–3 weeks for registration and vineyard
                    verification. You may not use the OVR Seal until OVR confirms
                    your registration and provides the official artwork.
                  </p>

                  <button type="submit" className="button seal-submit-button">
                    Review and Continue to Payment
                  </button>
                  {formik.submitCount > 0 && !formik.isValid && (
                    <span className="submit-error-heading">
                        There was an issue with your registration. Please correct the marked fields.
                      </span>
                  )}
                </Box>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};
